// src/components/admin/AddRecipe.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Alert,
  Box,
  TextField,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AddCircle, RemoveCircle } from "@mui/icons-material";
import Autocomplete from "@mui/material/Autocomplete";
import api from "../../services/api";
import { useTranslation } from "react-i18next";
import BackToDashboard from "../common/BackToAdminDashboard.js";

const LANGS = [
  "el", "en", "ar", "bg", "de", "en_gb", "en_us", "es", "fr", "hr", "it", "ro", "ru", "sq", "sr", "sv", "ur", "zh"
];

const initLangObj = () => Object.fromEntries(LANGS.map((l) => [l, ""]));

function toApiLang(lang) {
  // backend σου δέχεται gr και το κάνει el
  if (!lang) return "en";
  if (lang === "el") return "gr";
  return lang;
}

export default function AdminAddRecipe() {
  const { t, i18n } = useTranslation();

  // Translations state
  const [names, setNames] = useState(initLangObj);
  const [ingredientsByLang, setIngredientsByLang] = useState(initLangObj);
  const [instructionsByLang, setInstructionsByLang] = useState(initLangObj);

  // Relationships
  const [mealTypeIds, setMealTypeIds] = useState([]);
  const [foodTypeIds, setFoodTypeIds] = useState([]);
  const [countryRecipesId, setCountryRecipesId] = useState("");
  const [dietTypeIds, setDietTypeIds] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [religiousPreferenceIds, setReligiousPreferenceIds] = useState([]);

  // Reference data
  const [mealTypes, setMealTypes] = useState([]);
  const [foodTypes, setFoodTypes] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [dietTypes, setDietTypes] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [religiousPrefsList, setReligiousPrefsList] = useState([]);
  const [genericProductList, setGenericProductList] = useState([]);

  // Other fields
  const [difficulty, setDifficulty] = useState("");
  const [prepTime, setPrepTime] = useState("");
  const [calories, setCalories] = useState("");
  const [visibility, setVisibility] = useState("private");

  // Image
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");

  // Ingredients array (generic products)
  const [products, setProducts] = useState([{ generic_product_id: "", quantity: "" }]);

  // UI
  const [refDataLoading, setRefDataLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Fetch reference data
  useEffect(() => {
    let mounted = true;

    const fetchReferenceData = async () => {
      try {
        setRefDataLoading(true);
        setError("");

        const [
          mealTypesRes,
          foodTypesRes,
          countryRes,
          dietRes,
          seasonsRes,
          religiousRes,
          genericProdsRes,
        ] = await Promise.all([
          api.household.getMealTypes(),
          api.household.getFoodTypes(),
          api.household.getCountryRecipes(),
          api.household.getDietTypes(),
          api.household.getSeasons(),
          api.household.getReligiousPreferences(),
          api.genericProducts.list(),
        ]);

        if (!mounted) return;

        setMealTypes(Array.isArray(mealTypesRes?.data) ? mealTypesRes.data : []);
        setFoodTypes(Array.isArray(foodTypesRes?.data) ? foodTypesRes.data : []);
        setCountryList(Array.isArray(countryRes?.data) ? countryRes.data : []);
        setDietTypes(Array.isArray(dietRes?.data) ? dietRes.data : []);
        setSeasons(Array.isArray(seasonsRes?.data) ? seasonsRes.data : []);
        setReligiousPrefsList(Array.isArray(religiousRes?.data) ? religiousRes.data : []);

        // ✅ εδώ ήταν συχνά το bug: άλλοτε έρχεται {success,data:[]}, άλλοτε []
        const gpData =
          Array.isArray(genericProdsRes?.data) ? genericProdsRes.data :
            Array.isArray(genericProdsRes?.data?.data) ? genericProdsRes.data.data :
              [];
        setGenericProductList(gpData);
      } catch (err) {
        console.error(err);
        setError(t("failed_to_load_reference_data") || "Failed to load reference data.");
      } finally {
        setRefDataLoading(false);
      }
    };

    fetchReferenceData();
    return () => { mounted = false; };
  }, [t]);

  const getGpLabel = (gp) => {
    const lang = i18n.language === "gr" ? "el" : i18n.language;
    const key = (lang === "el") ? "name_el" : `name_${lang}`;
    return (gp?.[key] || gp?.name_en || gp?.name_el || "").toString();
  };

  const addProd = () => setProducts((prev) => [...prev, { generic_product_id: "", quantity: "" }]);
  const removeProd = (index) => setProducts((prev) => prev.filter((_, i) => i !== index));

  const handleProductChange = (index, field, value) => {
    setProducts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const buildTranslationsPayload = () => {
    const out = {};

    // name_*
    for (const lng of LANGS) out[`name_${lng}`] = (names[lng] || "").trim();

    // ingredients_*
    for (const lng of LANGS) out[`ingredients_${lng}`] = (ingredientsByLang[lng] || "").trim();

    // instructions_*
    for (const lng of LANGS) out[`instructions_${lng}`] = (instructionsByLang[lng] || "").trim();

    // ✅ required safety: αν λείπει en/el κάνε fallback
    if (!out.name_el && out.name_en) out.name_el = out.name_en;
    if (!out.name_en && out.name_el) out.name_en = out.name_el;

    return out;
  };

  const validateRequired = () => {
    const nameEl = (names.el || "").trim();
    const nameEn = (names.en || "").trim();
    if (!nameEl && !nameEn) {
      setError("Πρέπει να συμπληρώσεις τουλάχιστον name_el ή name_en.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateRequired()) return;

    setLoading(true);

    // upload image if needed
    let imageUrlToSend = imageUrl;
    if (imageFile) {
      try {
        const fd = new FormData();
        fd.append("image", imageFile);
        const up = await api.recipes.uploadImage(fd);
        if (up?.data?.success) {
          imageUrlToSend = up.data.image_url;
        } else {
          setError(up?.data?.error || "Image upload failed");
          setLoading(false);
          return;
        }
      } catch (err) {
        setError("Image upload failed");
        setLoading(false);
        return;
      }
    }

    try {
      const payload = {
        ...buildTranslationsPayload(),

        meal_type_ids: mealTypeIds.map((x) => parseInt(x, 10)),
        food_type_ids: foodTypeIds.map((x) => parseInt(x, 10)),
        country_recipes_id: countryRecipesId ? parseInt(countryRecipesId, 10) : null,
        diet_type_ids: dietTypeIds.map((x) => parseInt(x, 10)),
        season_id: seasonId ? parseInt(seasonId, 10) : null,
        religious_preference_ids: religiousPreferenceIds.map((x) => parseInt(x, 10)),

        difficulty: difficulty || null,
        prep_time: prepTime ? parseInt(prepTime, 10) : null,
        calories: calories ? parseInt(calories, 10) : null,
        image_url: imageUrlToSend || "",
        visibility,

        products: (products || []).map((p) => ({
          generic_product_id: p.generic_product_id ? parseInt(p.generic_product_id, 10) : null,
          quantity: p.quantity ? parseFloat(p.quantity) : 0,
        })),
      };

      const res = await api.recipes.add(payload);
      if (res?.data?.success) {
        setSuccess(t("recipe_added_successfully") || "Recipe added successfully!");
        // reset
        setNames(initLangObj());
        setIngredientsByLang(initLangObj());
        setInstructionsByLang(initLangObj());
        setMealTypeIds([]);
        setFoodTypeIds([]);
        setCountryRecipesId("");
        setDietTypeIds([]);
        setSeasonId("");
        setReligiousPreferenceIds([]);
        setDifficulty("");
        setPrepTime("");
        setCalories("");
        setVisibility("private");
        setImageFile(null);
        setImageUrl("");
        setProducts([{ generic_product_id: "", quantity: "" }]);
      } else {
        setError(res?.data?.error || t("error_adding_recipe") || "Error adding recipe.");
      }
    } catch (err) {
      console.error(err);
      setError(t("error_adding_recipe") || "Error adding recipe.");
    } finally {
      setLoading(false);
    }
  };

  const requiredLangs = useMemo(() => (["el", "en"]), []);

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <BackToDashboard dashboardPath="/admin/dashboard" />
      <Typography variant="h4" gutterBottom>
        {t("add_recipe_admin") || "Add Recipe (Admin)"}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {refDataLoading ? (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography>{t("loading_reference_data") || "Loading reference data..."}</Typography>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          {/* ===== Translations ===== */}
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Μεταφράσεις (όλα τα πεδία)
          </Typography>

          {/* Required: EL/EN always visible */}
          <Divider sx={{ mb: 2 }} />
          {requiredLangs.map((lng) => (
            <Box key={lng} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {lng.toUpperCase()} (required)
              </Typography>

              <TextField
                label={`name_${lng}`}
                variant="outlined"
                fullWidth
                required
                value={names[lng] || ""}
                onChange={(e) => setNames((p) => ({ ...p, [lng]: e.target.value }))}
                sx={{ mb: 1 }}
              />

              <TextField
                label={`ingredients_${lng}`}
                variant="outlined"
                fullWidth
                multiline
                minRows={3}
                value={ingredientsByLang[lng] || ""}
                onChange={(e) => setIngredientsByLang((p) => ({ ...p, [lng]: e.target.value }))}
                sx={{ mb: 1 }}
              />

              <TextField
                label={`instructions_${lng}`}
                variant="outlined"
                fullWidth
                multiline
                minRows={3}
                value={instructionsByLang[lng] || ""}
                onChange={(e) => setInstructionsByLang((p) => ({ ...p, [lng]: e.target.value }))}
              />
            </Box>
          ))}

          {/* Other languages in accordion */}
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Υπόλοιπες γλώσσες (optional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {LANGS.filter((l) => !requiredLangs.includes(l)).map((lng) => (
                <Box key={lng} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {lng.toUpperCase()}
                  </Typography>

                  <TextField
                    label={`name_${lng}`}
                    fullWidth
                    value={names[lng] || ""}
                    onChange={(e) => setNames((p) => ({ ...p, [lng]: e.target.value }))}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label={`ingredients_${lng}`}
                    fullWidth
                    multiline
                    minRows={3}
                    value={ingredientsByLang[lng] || ""}
                    onChange={(e) => setIngredientsByLang((p) => ({ ...p, [lng]: e.target.value }))}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label={`instructions_${lng}`}
                    fullWidth
                    multiline
                    minRows={3}
                    value={instructionsByLang[lng] || ""}
                    onChange={(e) => setInstructionsByLang((p) => ({ ...p, [lng]: e.target.value }))}
                  />
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>

          {/* ===== Relationships ===== */}
          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t("meal_type") || "Meal Type"}</InputLabel>
            <Select
              multiple
              value={mealTypeIds}
              onChange={(e) => setMealTypeIds(e.target.value)}
              label={t("meal_type") || "Meal Type"}
              renderValue={(selected) =>
                selected
                  .map((id) => mealTypes.find((m) => m.id === id)?.name || "")
                  .filter(Boolean)
                  .join(", ")
              }
            >
              {mealTypes.map((mt) => (
                <MenuItem key={mt.id} value={mt.id}>
                  {mt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t("food_type") || "Food Type"}</InputLabel>
            <Select
              multiple
              value={foodTypeIds}
              onChange={(e) => setFoodTypeIds(e.target.value)}
              label={t("food_type") || "Food Type"}
              renderValue={(selected) =>
                selected
                  .map((id) => foodTypes.find((f) => f.id === id)?.type_name || "")
                  .filter(Boolean)
                  .join(", ")
              }
            >
              {foodTypes.map((ft) => (
                <MenuItem key={ft.id} value={ft.id}>
                  {ft.type_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t("country_label") || "Country"}</InputLabel>
            <Select
              value={countryRecipesId}
              label={t("country_label") || "Country"}
              onChange={(e) => setCountryRecipesId(e.target.value)}
            >
              <MenuItem value="">
                <em>{t("select_a_country") || "Select a country"}</em>
              </MenuItem>
              {countryList.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.country_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t("diet_type_label") || "Diet Type"}</InputLabel>
            <Select
              multiple
              value={dietTypeIds}
              onChange={(e) => setDietTypeIds(e.target.value)}
              label={t("diet_type_label") || "Diet Type"}
              renderValue={(selected) =>
                selected
                  .map((id) => dietTypes.find((d) => d.id.toString() === id?.toString())?.diet_name || "")
                  .filter(Boolean)
                  .join(", ")
              }
            >
              {dietTypes.map((dt) => (
                <MenuItem key={dt.id} value={dt.id.toString()}>
                  {dt.diet_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t("season_label") || "Season"}</InputLabel>
            <Select
              value={seasonId}
              label={t("season_label") || "Season"}
              onChange={(e) => setSeasonId(e.target.value)}
            >
              <MenuItem value="">
                <em>{t("select_season") || "Select Season"}</em>
              </MenuItem>
              {seasons.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t("religious_preferences_label") || "Religious Preferences"}</InputLabel>
            <Select
              multiple
              value={religiousPreferenceIds}
              onChange={(e) => setReligiousPreferenceIds(e.target.value)}
              label={t("religious_preferences_label") || "Religious Preferences"}
              renderValue={(selected) =>
                selected
                  .map((id) => religiousPrefsList.find((rp) => rp.id === id)?.preference_name || "")
                  .filter(Boolean)
                  .join(", ")
              }
            >
              {religiousPrefsList.map((rp) => (
                <MenuItem key={rp.id} value={rp.id}>
                  {rp.preference_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ===== Other fields ===== */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Difficulty</InputLabel>
            <Select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} label="Difficulty">
              <MenuItem value=""><em>Select Difficulty</em></MenuItem>
              <MenuItem value="easy">Easy</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="hard">Hard</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Preparation Time (minutes)"
            value={prepTime}
            onChange={(e) => setPrepTime(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            type="number"
          />
          <TextField
            label="Calories"
            value={calories}
            onChange={(e) => setCalories(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            type="number"
          />

          {/* Image upload */}
          <Button variant="outlined" component="label" sx={{ mb: 1 }}>
            Upload Image
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            />
          </Button>
          {imageFile && <Typography variant="body2" sx={{ mb: 2 }}>{imageFile.name}</Typography>}

          {/* Visibility */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("visibility") || "Visibility"}</InputLabel>
            <Select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              label={t("visibility") || "Visibility"}
            >
              <MenuItem value="private">{t("private") || "Private"}</MenuItem>
              <MenuItem value="public">{t("public") || "Public"}</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>

          {/* ===== Generic Products ===== */}
          <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
            {t("ingredients_label") || "Ingredients (Generic Products)"}
          </Typography>

          {(products || []).map((prod, index) => (
            <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
              <Grid item xs={12} md={5}>
                <Autocomplete
                  options={Array.isArray(genericProductList) ? genericProductList : []}
                  getOptionLabel={(option) => getGpLabel(option)}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  value={
                    (Array.isArray(genericProductList) ? genericProductList : []).find(
                      (gp) => gp.id?.toString() === prod.generic_product_id?.toString()
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleProductChange(index, "generic_product_id", newValue ? newValue.id.toString() : "");
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={t("generic_product_label") || "Generic Product"} required />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={5}>
                <TextField
                  label={t("quantity") || "Quantity"}
                  variant="outlined"
                  fullWidth
                  required
                  value={prod.quantity}
                  onChange={(e) => handleProductChange(index, "quantity", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={2} sx={{ display: "flex", alignItems: "center" }}>
                {index === 0 ? (
                  <IconButton color="primary" onClick={addProd}>
                    <AddCircle />
                  </IconButton>
                ) : (
                  <IconButton color="error" onClick={() => removeProd(index)}>
                    <RemoveCircle />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}

          <Button variant="contained" type="submit" sx={{ mt: 3 }} disabled={loading}>
            {loading ? (t("adding") || "Adding...") : (t("add_recipe_admin") || "Add Recipe")}
          </Button>
        </Box>
      )}
    </Container>
  );
}
